import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { 
  Plus, Pencil, Trash2, Target, BarChart3, Settings2, 
  ChevronRight, AlertTriangle, CheckCircle, Info,
  Calculator, Clock, Users, Percent
} from "lucide-react";

export default function KRASettings({ user }) {
  const [activeTab, setActiveTab] = useState("kra-types");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [kraTypes, setKraTypes] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedKraType, setSelectedKraType] = useState(null);
  const [weightageSummary, setWeightageSummary] = useState(null);
  
  // KRA Type Dialog State
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeFormData, setTypeFormData] = useState({
    name: "",
    description: "",
    department_id: "",
    department_name: "",
    applies_to: ["employee", "intern"],
    review_frequency: "monthly"
  });
  
  // Line Item Dialog State
  const [lineItemDialogOpen, setLineItemDialogOpen] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState(null);
  const [lineItemFormData, setLineItemFormData] = useState({
    name: "",
    description: "",
    weightage: "",
    measurement_unit: "hours",
    measurement_type: "higher_is_better",
    data_source: {
      type: "manual",
      auto_source: null,
      responsible_department_id: null,
      responsible_user_id: null
    },
    calculation_rules: {
      type: "slab_based",
      minimum_threshold: 0,
      slabs: [
        { min_percent: 0, max_percent: 69, weightage_multiplier: 0, label: "Not Met" },
        { min_percent: 70, max_percent: 84, weightage_multiplier: 0.7, label: "Partial" },
        { min_percent: 85, max_percent: 99, weightage_multiplier: 0.9, label: "Near Target" },
        { min_percent: 100, max_percent: 110, weightage_multiplier: 1.0, label: "On Target" },
        { min_percent: 111, max_percent: 999, weightage_multiplier: 1.2, label: "Exceeds" }
      ],
      max_weightage_multiplier: 1.2
    },
    sort_order: 1
  });

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });

  // Permissions
  const permissions = user?.permissions?.kraSettings || {};
  const canCreate = permissions.create;
  const canEdit = permissions.edit;
  const canDelete = permissions.delete;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedKraType) {
      fetchLineItems(selectedKraType.id);
      fetchWeightageSummary(selectedKraType.id);
    }
  }, [selectedKraType]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [typesRes, deptRes] = await Promise.all([
        api.get("/kra-types"),
        api.get("/departments-list")
      ]);
      setKraTypes(typesRes.data);
      setDepartments(deptRes.data);
      
      // Select first KRA type if exists
      if (typesRes.data.length > 0) {
        setSelectedKraType(typesRes.data[0]);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLineItems = async (kraTypeId) => {
    try {
      const res = await api.get(`/kra-line-items?kra_type_id=${kraTypeId}`);
      setLineItems(res.data);
    } catch (error) {
      toast.error("Failed to load line items");
    }
  };

  const fetchWeightageSummary = async (kraTypeId) => {
    try {
      const res = await api.get(`/kra-types/${kraTypeId}/weightage-summary`);
      setWeightageSummary(res.data);
    } catch (error) {
      console.error("Failed to load weightage summary");
    }
  };

  // KRA Type Handlers
  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      const dept = departments.find(d => d.id === typeFormData.department_id);
      const payload = {
        ...typeFormData,
        department_name: dept?.name || typeFormData.department_id
      };

      if (editingType) {
        await api.put(`/kra-types/${editingType.id}`, payload);
        toast.success("KRA type updated successfully");
      } else {
        await api.post("/kra-types", payload);
        toast.success("KRA type created successfully");
      }
      setTypeDialogOpen(false);
      resetTypeForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save KRA type");
    }
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setTypeFormData({
      name: type.name,
      description: type.description || "",
      department_id: type.department_id,
      department_name: type.department_name,
      applies_to: type.applies_to || ["employee", "intern"],
      review_frequency: type.review_frequency || "monthly"
    });
    setTypeDialogOpen(true);
  };

  const handleDeleteType = async () => {
    try {
      await api.delete(`/kra-types/${deleteDialog.item.id}`);
      toast.success("KRA type deleted successfully");
      setDeleteDialog({ open: false, type: null, item: null });
      fetchData();
      if (selectedKraType?.id === deleteDialog.item.id) {
        setSelectedKraType(null);
        setLineItems([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete KRA type");
    }
  };

  const resetTypeForm = () => {
    setEditingType(null);
    setTypeFormData({
      name: "",
      description: "",
      department_id: "",
      department_name: "",
      applies_to: ["employee", "intern"],
      review_frequency: "monthly"
    });
  };

  // Line Item Handlers
  const handleLineItemSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...lineItemFormData,
        kra_type_id: selectedKraType.id,
        weightage: parseFloat(lineItemFormData.weightage)
      };

      if (editingLineItem) {
        await api.put(`/kra-line-items/${editingLineItem.id}`, payload);
        toast.success("Line item updated successfully");
      } else {
        await api.post("/kra-line-items", payload);
        toast.success("Line item created successfully");
      }
      setLineItemDialogOpen(false);
      resetLineItemForm();
      fetchLineItems(selectedKraType.id);
      fetchWeightageSummary(selectedKraType.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save line item");
    }
  };

  const handleEditLineItem = (item) => {
    setEditingLineItem(item);
    setLineItemFormData({
      name: item.name,
      description: item.description || "",
      weightage: item.weightage.toString(),
      measurement_unit: item.measurement_unit,
      measurement_type: item.measurement_type,
      data_source: item.data_source || { type: "manual" },
      calculation_rules: item.calculation_rules || {
        type: "slab_based",
        minimum_threshold: 0,
        slabs: [],
        max_weightage_multiplier: 1.0
      },
      sort_order: item.sort_order || 1
    });
    setLineItemDialogOpen(true);
  };

  const handleDeleteLineItem = async () => {
    try {
      await api.delete(`/kra-line-items/${deleteDialog.item.id}`);
      toast.success("Line item deleted successfully");
      setDeleteDialog({ open: false, type: null, item: null });
      fetchLineItems(selectedKraType.id);
      fetchWeightageSummary(selectedKraType.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete line item");
    }
  };

  const resetLineItemForm = () => {
    setEditingLineItem(null);
    setLineItemFormData({
      name: "",
      description: "",
      weightage: "",
      measurement_unit: "hours",
      measurement_type: "higher_is_better",
      data_source: { type: "manual" },
      calculation_rules: {
        type: "slab_based",
        minimum_threshold: 0,
        slabs: [
          { min_percent: 0, max_percent: 69, weightage_multiplier: 0, label: "Not Met" },
          { min_percent: 70, max_percent: 84, weightage_multiplier: 0.7, label: "Partial" },
          { min_percent: 85, max_percent: 99, weightage_multiplier: 0.9, label: "Near Target" },
          { min_percent: 100, max_percent: 110, weightage_multiplier: 1.0, label: "On Target" },
          { min_percent: 111, max_percent: 999, weightage_multiplier: 1.2, label: "Exceeds" }
        ],
        max_weightage_multiplier: 1.2
      },
      sort_order: 1
    });
  };

  const updateSlab = (index, field, value) => {
    const newSlabs = [...lineItemFormData.calculation_rules.slabs];
    newSlabs[index] = { ...newSlabs[index], [field]: field.includes('percent') || field === 'weightage_multiplier' ? parseFloat(value) || 0 : value };
    setLineItemFormData({
      ...lineItemFormData,
      calculation_rules: {
        ...lineItemFormData.calculation_rules,
        slabs: newSlabs
      }
    });
  };

  const addSlab = () => {
    const slabs = lineItemFormData.calculation_rules.slabs;
    const lastSlab = slabs[slabs.length - 1];
    setLineItemFormData({
      ...lineItemFormData,
      calculation_rules: {
        ...lineItemFormData.calculation_rules,
        slabs: [
          ...slabs,
          { 
            min_percent: lastSlab ? lastSlab.max_percent + 1 : 0, 
            max_percent: lastSlab ? lastSlab.max_percent + 20 : 100, 
            weightage_multiplier: 1.0, 
            label: "New Slab" 
          }
        ]
      }
    });
  };

  const removeSlab = (index) => {
    const newSlabs = lineItemFormData.calculation_rules.slabs.filter((_, i) => i !== index);
    setLineItemFormData({
      ...lineItemFormData,
      calculation_rules: {
        ...lineItemFormData.calculation_rules,
        slabs: newSlabs
      }
    });
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-md3-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Page Header - MD3 Style */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-md3-headline-medium text-md3-on-surface font-medium">KRA Settings</h1>
            <p className="text-md3-body-medium text-md3-on-surface-variant mt-1">
              Configure Key Result Areas, line items, weightages and calculation rules
            </p>
          </div>
        </div>

        {/* Main Content - Two Panel Layout */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Panel - KRA Types List */}
          <div className="col-span-4">
            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-md3-title-large text-md3-on-surface">KRA Types</CardTitle>
                  {canCreate && (
                    <Button 
                      size="sm" 
                      onClick={() => { resetTypeForm(); setTypeDialogOpen(true); }}
                      className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  )}
                </div>
                <CardDescription className="text-md3-body-small text-md3-on-surface-variant">
                  Select a KRA type to manage its line items
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-md3-outline-variant">
                  {kraTypes.length === 0 ? (
                    <div className="p-6 text-center text-md3-body-medium text-md3-on-surface-variant">
                      No KRA types defined yet. Create your first KRA type to get started.
                    </div>
                  ) : (
                    kraTypes.map((type) => (
                      <div
                        key={type.id}
                        className={`p-4 cursor-pointer hover:bg-md3-surface-container transition-colors ${
                          selectedKraType?.id === type.id ? 'bg-md3-secondary-container' : ''
                        }`}
                        onClick={() => setSelectedKraType(type)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Target className="h-4 w-4 text-md3-primary flex-shrink-0" />
                              <span className="text-md3-body-large font-medium text-md3-on-surface truncate">
                                {type.name}
                              </span>
                              {!type.is_active && (
                                <Badge variant="outline" className="text-md3-error border-md3-error">Inactive</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="bg-md3-secondary-container text-md3-on-secondary-container text-xs">
                                {type.department_name}
                              </Badge>
                              <span className="text-md3-body-small text-md3-on-surface-variant">
                                {type.review_frequency}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            {canEdit && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={(e) => { e.stopPropagation(); handleEditType(type); }}
                                className="h-8 w-8 p-0 text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, type: 'kra-type', item: type }); }}
                                className="h-8 w-8 p-0 text-md3-error hover:bg-md3-error-container rounded-md3-full"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                            <ChevronRight className={`h-4 w-4 text-md3-on-surface-variant transition-transform ${
                              selectedKraType?.id === type.id ? 'rotate-90' : ''
                            }`} />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Line Items */}
          <div className="col-span-8">
            {selectedKraType ? (
              <div className="space-y-4">
                {/* Weightage Summary Card */}
                {weightageSummary && (
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-md3-full bg-md3-primary-container flex items-center justify-center">
                            <Percent className="h-5 w-5 text-md3-on-primary-container" />
                          </div>
                          <div>
                            <h3 className="text-md3-title-medium text-md3-on-surface">
                              {selectedKraType.name}
                            </h3>
                            <p className="text-md3-body-small text-md3-on-surface-variant">
                              {selectedKraType.department_name} • {selectedKraType.review_frequency}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {weightageSummary.is_complete ? (
                            <Badge className="bg-md3-tertiary-container text-md3-on-tertiary-container">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Complete (100%)
                            </Badge>
                          ) : (
                            <Badge className="bg-md3-error-container text-md3-on-error-container">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              {weightageSummary.remaining_weightage}% Remaining
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-md3-body-small">
                          <span className="text-md3-on-surface-variant">Total Weightage</span>
                          <span className="text-md3-on-surface font-medium">{weightageSummary.total_weightage}%</span>
                        </div>
                        <Progress 
                          value={weightageSummary.total_weightage} 
                          className="h-2 bg-md3-surface-container"
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Line Items Table */}
                <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-md3-title-large text-md3-on-surface">Line Items</CardTitle>
                        <CardDescription className="text-md3-body-small text-md3-on-surface-variant">
                          KRA metrics with weightage and calculation rules
                        </CardDescription>
                      </div>
                      {canCreate && (
                        <Button 
                          onClick={() => { resetLineItemForm(); setLineItemDialogOpen(true); }}
                          className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Line Item
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {lineItems.length === 0 ? (
                      <div className="text-center py-8 text-md3-body-medium text-md3-on-surface-variant">
                        <BarChart3 className="h-12 w-12 mx-auto mb-3 text-md3-outline" />
                        <p>No line items defined for this KRA type.</p>
                        <p className="text-md3-body-small mt-1">Add line items to define measurable metrics.</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-md3-outline-variant hover:bg-transparent">
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant">#</TableHead>
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant">Name</TableHead>
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant">Weightage</TableHead>
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant">Unit</TableHead>
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant">Data Source</TableHead>
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant">Calc Type</TableHead>
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {lineItems.map((item, index) => (
                            <TableRow key={item.id} className="border-md3-outline-variant">
                              <TableCell className="text-md3-body-medium text-md3-on-surface-variant">
                                {index + 1}
                              </TableCell>
                              <TableCell>
                                <div>
                                  <span className="text-md3-body-medium text-md3-on-surface font-medium">
                                    {item.name}
                                  </span>
                                  {item.description && (
                                    <p className="text-md3-body-small text-md3-on-surface-variant truncate max-w-xs">
                                      {item.description}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className="bg-md3-primary-container text-md3-on-primary-container">
                                  {item.weightage}%
                                </Badge>
                              </TableCell>
                              <TableCell className="text-md3-body-medium text-md3-on-surface">
                                {item.measurement_unit}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={
                                  item.data_source?.type === 'auto' 
                                    ? 'border-md3-tertiary text-md3-tertiary' 
                                    : 'border-md3-outline text-md3-on-surface-variant'
                                }>
                                  {item.data_source?.type === 'auto' ? item.data_source?.auto_source : 'Manual'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-md3-body-medium text-md3-on-surface">
                                {item.calculation_rules?.type?.replace('_', ' ')}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {canEdit && (
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      onClick={() => handleEditLineItem(item)}
                                      className="h-8 w-8 p-0 text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {canDelete && (
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      onClick={() => setDeleteDialog({ open: true, type: 'line-item', item })}
                                      className="h-8 w-8 p-0 text-md3-error hover:bg-md3-error-container rounded-md3-full"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Settings2 className="h-16 w-16 text-md3-outline mb-4" />
                  <h3 className="text-md3-title-large text-md3-on-surface mb-2">Select a KRA Type</h3>
                  <p className="text-md3-body-medium text-md3-on-surface-variant text-center max-w-md">
                    Choose a KRA type from the left panel to view and manage its line items, or create a new KRA type to get started.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* KRA Type Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              {editingType ? 'Edit KRA Type' : 'Create KRA Type'}
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              Define a Key Result Area type for a department
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTypeSubmit} className="space-y-4">
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Name *</Label>
              <Input
                value={typeFormData.name}
                onChange={(e) => setTypeFormData({ ...typeFormData, name: e.target.value })}
                placeholder="e.g., Technical Performance"
                required
                className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
              />
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Department *</Label>
              <Select 
                value={typeFormData.department_id} 
                onValueChange={(v) => setTypeFormData({ ...typeFormData, department_id: v })}
                required
              >
                <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(dept => (
                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Description</Label>
              <Textarea
                value={typeFormData.description}
                onChange={(e) => setTypeFormData({ ...typeFormData, description: e.target.value })}
                placeholder="Describe what this KRA type measures..."
                className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                rows={2}
              />
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Review Frequency *</Label>
              <Select 
                value={typeFormData.review_frequency} 
                onValueChange={(v) => setTypeFormData({ ...typeFormData, review_frequency: v })}
              >
                <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="half-yearly">Half-Yearly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Applies To</Label>
              <div className="flex items-center gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={typeFormData.applies_to.includes('employee')}
                    onCheckedChange={(checked) => {
                      const newApplies = checked 
                        ? [...typeFormData.applies_to, 'employee']
                        : typeFormData.applies_to.filter(a => a !== 'employee');
                      setTypeFormData({ ...typeFormData, applies_to: newApplies });
                    }}
                  />
                  <span className="text-md3-body-medium text-md3-on-surface">Employees</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={typeFormData.applies_to.includes('intern')}
                    onCheckedChange={(checked) => {
                      const newApplies = checked 
                        ? [...typeFormData.applies_to, 'intern']
                        : typeFormData.applies_to.filter(a => a !== 'intern');
                      setTypeFormData({ ...typeFormData, applies_to: newApplies });
                    }}
                  />
                  <span className="text-md3-body-medium text-md3-on-surface">Interns</span>
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setTypeDialogOpen(false)}
                className="border-md3-outline text-md3-primary rounded-md3-full"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
              >
                {editingType ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Line Item Dialog */}
      <Dialog open={lineItemDialogOpen} onOpenChange={setLineItemDialogOpen}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              {editingLineItem ? 'Edit Line Item' : 'Add Line Item'}
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              Define a measurable metric for {selectedKraType?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLineItemSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-md3-label-large text-md3-on-surface">Name *</Label>
                <Input
                  value={lineItemFormData.name}
                  onChange={(e) => setLineItemFormData({ ...lineItemFormData, name: e.target.value })}
                  placeholder="e.g., Billable Hours"
                  required
                  className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                />
              </div>
              <div>
                <Label className="text-md3-label-large text-md3-on-surface">
                  Weightage (%) *
                  {weightageSummary && (
                    <span className="text-md3-body-small text-md3-on-surface-variant ml-2">
                      (Available: {weightageSummary.remaining_weightage}%)
                    </span>
                  )}
                </Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={lineItemFormData.weightage}
                  onChange={(e) => setLineItemFormData({ ...lineItemFormData, weightage: e.target.value })}
                  required
                  className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                />
              </div>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Description</Label>
              <Textarea
                value={lineItemFormData.description}
                onChange={(e) => setLineItemFormData({ ...lineItemFormData, description: e.target.value })}
                placeholder="Describe how this metric is measured..."
                className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-md3-label-large text-md3-on-surface">Measurement Unit *</Label>
                <Select 
                  value={lineItemFormData.measurement_unit} 
                  onValueChange={(v) => setLineItemFormData({ ...lineItemFormData, measurement_unit: v })}
                >
                  <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="count">Count</SelectItem>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="currency">Currency</SelectItem>
                    <SelectItem value="rating">Rating (1-5)</SelectItem>
                    <SelectItem value="score">Score</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-md3-label-large text-md3-on-surface">Measurement Type *</Label>
                <Select 
                  value={lineItemFormData.measurement_type} 
                  onValueChange={(v) => setLineItemFormData({ ...lineItemFormData, measurement_type: v })}
                >
                  <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="higher_is_better">Higher is Better</SelectItem>
                    <SelectItem value="lower_is_better">Lower is Better</SelectItem>
                    <SelectItem value="target_match">Target Match</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Data Source *</Label>
              <Select 
                value={lineItemFormData.data_source.type} 
                onValueChange={(v) => setLineItemFormData({ 
                  ...lineItemFormData, 
                  data_source: { ...lineItemFormData.data_source, type: v }
                })}
              >
                <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual Entry (HR/Department)</SelectItem>
                  <SelectItem value="auto">Auto-Populate from System</SelectItem>
                </SelectContent>
              </Select>
              {lineItemFormData.data_source.type === 'auto' && (
                <div className="mt-2">
                  <Select 
                    value={lineItemFormData.data_source.auto_source || ''} 
                    onValueChange={(v) => setLineItemFormData({ 
                      ...lineItemFormData, 
                      data_source: { ...lineItemFormData.data_source, auto_source: v }
                    })}
                  >
                    <SelectTrigger className="bg-md3-surface border-md3-outline rounded-md3-sm">
                      <SelectValue placeholder="Select data source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="timesheets">Timesheets (Billable Hours)</SelectItem>
                      <SelectItem value="error_tracker">Error Tracker (Quality Metrics)</SelectItem>
                      <SelectItem value="estimation_count">Estimation Count (Sales)</SelectItem>
                      <SelectItem value="estimation_value">Estimation Value (Sales)</SelectItem>
                      <SelectItem value="awarded_count">Awarded Project Count (Sales)</SelectItem>
                      <SelectItem value="awarded_value">Awarded Project Value (Sales)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Calculation Rules - Slabs */}
            <div className="border border-md3-outline-variant rounded-md3-md p-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-md3-label-large text-md3-on-surface">Calculation Slabs</Label>
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline"
                  onClick={addSlab}
                  className="border-md3-outline text-md3-primary rounded-md3-full"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Slab
                </Button>
              </div>
              <p className="text-md3-body-small text-md3-on-surface-variant mb-3">
                Define how achievement percentage converts to weightage multiplier
              </p>
              <div className="space-y-2">
                {lineItemFormData.calculation_rules.slabs.map((slab, index) => (
                  <div key={index} className="flex items-center gap-2 bg-md3-surface-container p-2 rounded-md3-sm">
                    <Input
                      type="number"
                      value={slab.min_percent}
                      onChange={(e) => updateSlab(index, 'min_percent', e.target.value)}
                      placeholder="Min %"
                      className="w-20 bg-md3-surface border-md3-outline rounded-md3-sm text-sm"
                    />
                    <span className="text-md3-on-surface-variant">to</span>
                    <Input
                      type="number"
                      value={slab.max_percent}
                      onChange={(e) => updateSlab(index, 'max_percent', e.target.value)}
                      placeholder="Max %"
                      className="w-20 bg-md3-surface border-md3-outline rounded-md3-sm text-sm"
                    />
                    <span className="text-md3-on-surface-variant">%</span>
                    <span className="text-md3-on-surface-variant">=</span>
                    <Input
                      type="number"
                      step="0.1"
                      value={slab.weightage_multiplier}
                      onChange={(e) => updateSlab(index, 'weightage_multiplier', e.target.value)}
                      placeholder="Multiplier"
                      className="w-20 bg-md3-surface border-md3-outline rounded-md3-sm text-sm"
                    />
                    <span className="text-md3-on-surface-variant">×</span>
                    <Input
                      value={slab.label}
                      onChange={(e) => updateSlab(index, 'label', e.target.value)}
                      placeholder="Label"
                      className="flex-1 bg-md3-surface border-md3-outline rounded-md3-sm text-sm"
                    />
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="ghost"
                      onClick={() => removeSlab(index)}
                      className="h-8 w-8 p-0 text-md3-error hover:bg-md3-error-container rounded-md3-full"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setLineItemDialogOpen(false)}
                className="border-md3-outline text-md3-primary rounded-md3-full"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
              >
                {editingLineItem ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              Delete {deleteDialog.type === 'kra-type' ? 'KRA Type' : 'Line Item'}
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              Are you sure you want to delete "{deleteDialog.item?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, type: null, item: null })}
              className="border-md3-outline text-md3-primary rounded-md3-full"
            >
              Cancel
            </Button>
            <Button 
              onClick={deleteDialog.type === 'kra-type' ? handleDeleteType : handleDeleteLineItem}
              className="bg-md3-error hover:bg-md3-error/90 text-md3-on-error rounded-md3-full"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
